import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Input, Type } from '@/components/form/Input';
import { Button } from '@/components/ui/button';
import {
  type BearerTokenFormData,
  RefreshIntervalUnit,
} from '@/sections/asset/components/tabs/credentials/credentialForms/types';

interface BearerTokenFormProps {
  formData: BearerTokenFormData;
  onChange: React.Dispatch<React.SetStateAction<BearerTokenFormData>>;
}

export const BearerTokenForm: React.FC<BearerTokenFormProps> = ({
  formData,
  onChange,
}) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="text-chariot-text-primary mb-3 block text-sm font-medium">
          Choose your token type
        </label>
        <div className="flex gap-4">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="tokenType"
              checked={!formData.isDynamic}
              onChange={() => onChange(prev => ({ ...prev, isDynamic: false }))}
              className="text-chariot-brand-primary focus:ring-chariot-brand-primary"
            />
            <span className="text-chariot-text-primary text-sm">Fixed</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="tokenType"
              checked={formData.isDynamic}
              onChange={() => onChange(prev => ({ ...prev, isDynamic: true }))}
              className="text-chariot-brand-primary focus:ring-chariot-brand-primary"
            />
            <span className="text-chariot-text-primary text-sm">Dynamic</span>
          </label>
        </div>
      </div>

      {!formData.isDynamic ? (
        <div className="border-chariot-stroke space-y-4 rounded-lg border p-4">
          <h3 className="text-chariot-text-primary text-sm font-bold">
            Fixed token
          </h3>
          <p className="text-chariot-text-secondary text-sm">
            An access token that does not change. Manually configure the token.
          </p>

          <Input
            label="Label"
            name="label"
            value={formData.label}
            onChange={e =>
              onChange(prev => ({ ...prev, label: e.target.value }))
            }
            placeholder="Enter label"
            data-testid="bearer-label-input"
          />

          <Input
            label="Token"
            name="token"
            type={Type.PASSWORD}
            value={formData.token}
            onChange={e =>
              onChange(prev => ({ ...prev, token: e.target.value }))
            }
            placeholder="Enter bearer token"
            required
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            data-testid="bearer-token-input"
          />
        </div>
      ) : (
        <div className="border-chariot-stroke space-y-4 rounded-lg border p-4">
          <h3 className="text-chariot-text-primary text-sm font-bold">
            Dynamic token
          </h3>
          <p className="text-chariot-text-secondary text-sm">
            Automatically retrieve and refresh the token from a service.
          </p>

          <Input
            label="Label"
            name="label"
            value={formData.label}
            onChange={e =>
              onChange(prev => ({ ...prev, label: e.target.value }))
            }
            placeholder="Enter label"
            data-testid="bearer-label-dynamic-input"
          />

          <Input
            label="Token Service URL"
            name="tokenServiceUrl"
            value={formData.dynamicConfig.tokenServiceUrl}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                dynamicConfig: {
                  ...prev.dynamicConfig,
                  tokenServiceUrl: e.target.value,
                },
              }))
            }
            placeholder="https://api.example.com/token"
            required
            data-testid="token-service-url-input"
          />

          <Input
            label="Request Method"
            name="requestMethod"
            type={Type.SELECT}
            value={formData.dynamicConfig.requestMethod}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                dynamicConfig: {
                  ...prev.dynamicConfig,
                  requestMethod: e.target.value as 'post' | 'get',
                },
              }))
            }
            options={[
              { label: 'POST', value: 'post' },
              { label: 'GET', value: 'get' },
            ]}
            data-testid="request-method-input"
          />

          <div>
            <label className="text-chariot-text-primary mb-2 block text-sm font-medium">
              Request Headers
            </label>
            {formData.dynamicConfig.requestHeaders.map((header, index) => (
              <div key={index} className="mb-2 flex gap-2">
                <input
                  type="text"
                  placeholder="Header name"
                  value={header.key}
                  onChange={e => {
                    onChange(prev => {
                      const newHeaders = [...prev.dynamicConfig.requestHeaders];
                      newHeaders[index].key = e.target.value;
                      return {
                        ...prev,
                        dynamicConfig: {
                          ...prev.dynamicConfig,
                          requestHeaders: newHeaders,
                        },
                      };
                    });
                  }}
                  className="border-chariot-stroke bg-chariot-background-primary flex-1 rounded-xs border p-1.5 pl-3"
                />
                <input
                  type="text"
                  placeholder="Header value"
                  value={header.value}
                  onChange={e => {
                    onChange(prev => {
                      const newHeaders = [...prev.dynamicConfig.requestHeaders];
                      newHeaders[index].value = e.target.value;
                      return {
                        ...prev,
                        dynamicConfig: {
                          ...prev.dynamicConfig,
                          requestHeaders: newHeaders,
                        },
                      };
                    });
                  }}
                  className="border-chariot-stroke bg-chariot-background-primary flex-1 rounded-xs border p-1.5 pl-3"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onChange(prev => {
                      const newHeaders =
                        prev.dynamicConfig.requestHeaders.filter(
                          (_, i) => i !== index
                        );
                      return {
                        ...prev,
                        dynamicConfig: {
                          ...prev.dynamicConfig,
                          requestHeaders: newHeaders,
                        },
                      };
                    });
                  }}
                >
                  <TrashIcon className="size-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange(prev => ({
                  ...prev,
                  dynamicConfig: {
                    ...prev.dynamicConfig,
                    requestHeaders: [
                      ...prev.dynamicConfig.requestHeaders,
                      { key: '', value: '' },
                    ],
                  },
                }));
              }}
              data-testid="add-header-button"
            >
              <PlusIcon className="size-4" />
              Add Header
            </Button>
          </div>

          <Input
            label="Request Body"
            name="requestBody"
            type={Type.TEXT_AREA}
            value={formData.dynamicConfig.requestBody}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                dynamicConfig: {
                  ...prev.dynamicConfig,
                  requestBody: e.target.value,
                },
              }))
            }
            placeholder='{"username": "user", "password": "pass"}'
            data-testid="request-body-input"
          />

          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                label="Refresh Interval"
                name="refreshInterval"
                value={formData.dynamicConfig.refreshInterval}
                onChange={e =>
                  onChange(prev => ({
                    ...prev,
                    dynamicConfig: {
                      ...prev.dynamicConfig,
                      refreshInterval: Number(e.target.value),
                    },
                  }))
                }
                placeholder="60"
                required
                data-testid="refresh-interval-input"
              />
            </div>
            <div className="flex-1">
              <Input
                label="Unit"
                name="refreshIntervalUnit"
                type={Type.SELECT}
                value={formData.dynamicConfig.refreshIntervalUnit}
                onChange={e =>
                  onChange(prev => ({
                    ...prev,
                    dynamicConfig: {
                      ...prev.dynamicConfig,
                      refreshIntervalUnit: e.target
                        .value as RefreshIntervalUnit,
                    },
                  }))
                }
                options={[
                  { label: 'Seconds', value: RefreshIntervalUnit.SECONDS },
                  { label: 'Minutes', value: RefreshIntervalUnit.MINUTES },
                  { label: 'Hours', value: RefreshIntervalUnit.HOURS },
                ]}
                data-testid="refresh-interval-unit-input"
              />
            </div>
          </div>

          <Input
            label="Token Location (JSON Path)"
            name="tokenLocation"
            value={formData.dynamicConfig.tokenLocation}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                dynamicConfig: {
                  ...prev.dynamicConfig,
                  tokenLocation: e.target.value,
                },
              }))
            }
            placeholder="access_token or data.token"
            required
            data-testid="token-location-input"
          />
        </div>
      )}
    </div>
  );
};
